import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, Clock, CheckCircle2, Wallet } from "lucide-react";
import { Calendar } from "../components/ui/calendar";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { api } from "../lib/api";
import { toast } from "sonner";

const PROGRAMS = [
  { name: "1-on-1 Training", price: 45 },
  { name: "Small Group Training", price: 35 },
  { name: "Large Group Training", price: 25 },
];

const formatDate = (d) => {
  if (!d) return "";
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};
const isWeekend = (d) => d.getDay() === 0 || d.getDay() === 6;
const isPast = (d) => {
  const t = new Date();
  t.setHours(0, 0, 0, 0);
  return d < t;
};

export default function BookingPage() {
  const [searchParams] = useSearchParams();
  const programFromQuery = searchParams.get("program");

  const [selectedDate, setSelectedDate] = useState(null);
  const [availability, setAvailability] = useState({
    all_slots: [],
    available: [],
    booked: [],
  });
  const [slot, setSlot] = useState("");
  const initialProgram =
    PROGRAMS.find((p) => p.name === programFromQuery)?.name || PROGRAMS[0].name;
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    program: initialProgram,
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [confirmed, setConfirmed] = useState(null);

  const dateStr = useMemo(() => formatDate(selectedDate), [selectedDate]);
  const selectedProgram = PROGRAMS.find((p) => p.name === form.program);

  useEffect(() => {
    const fetchAvailability = async () => {
      if (!dateStr) return;
      try {
        const { data } = await api.get(`/availability?date=${dateStr}`);
        setAvailability(data);
        setSlot("");
      } catch (e) {
        console.error(e);
      }
    };
    fetchAvailability();
  }, [dateStr]);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!selectedDate || !slot) {
      toast.error("Pick a date and time slot.");
      return;
    }
    if (!form.name || !form.email || !form.phone) {
      toast.error("Please fill in name, email, and phone.");
      return;
    }
    try {
      setSubmitting(true);
      const { data } = await api.post("/bookings", {
        ...form,
        date: dateStr,
        time_slot: slot,
      });
      setConfirmed(data);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      const detail = err?.response?.data?.detail || "Booking failed.";
      toast.error(detail);
    } finally {
      setSubmitting(false);
    }
  };

  if (confirmed) {
    return (
      <div data-testid="booking-confirmed" className="bg-[#050505] text-white min-h-screen">
        <Navbar />
        <main className="pt-28 pb-20 max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-[#070B14] border border-white/5 p-10 md:p-14 text-center">
            <CheckCircle2 size={56} className="text-[#2563EB] mx-auto" />
            <div className="mt-6 text-[10px] tracking-[0.3em] uppercase text-[#60A5FA]">
              [ Session Confirmed ]
            </div>
            <h1 className="font-display mt-3 text-3xl md:text-4xl font-bold uppercase tracking-tight">
              You're On The Floor
            </h1>
            <p className="mt-4 text-[#A1A1AA] max-w-md mx-auto">
              Hey {confirmed.name}, your training session is locked in. Save
              the details below — and bring your A-game.
            </p>

            <div className="mt-8 bg-[#050505] border border-white/5 p-6 text-left max-w-md mx-auto">
              <div className="text-[10px] tracking-[0.3em] uppercase text-white/50">
                Your Session
              </div>
              <div className="mt-3 flex items-center justify-between text-sm">
                <span className="text-white/70">Date</span>
                <span className="text-white font-medium">{confirmed.date}</span>
              </div>
              <div className="mt-2 flex items-center justify-between text-sm">
                <span className="text-white/70">Time</span>
                <span className="text-[#60A5FA] font-medium">{confirmed.time_slot}</span>
              </div>
              <div className="mt-2 flex items-center justify-between text-sm">
                <span className="text-white/70">Program</span>
                <span className="text-white">{confirmed.program}</span>
              </div>
              <div className="mt-2 flex items-center justify-between text-sm">
                <span className="text-white/70">Amount</span>
                <span className="text-white font-display text-lg">
                  ${confirmed.amount?.toFixed(2)}
                </span>
              </div>
            </div>

            <div className="mt-6 bg-[#0a1020] border border-[#2563EB]/40 p-5 text-left max-w-md mx-auto">
              <div className="flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase text-[#60A5FA]">
                <Wallet size={14} /> Payment Instructions
              </div>
              <div className="mt-2 text-white">
                Pay <span className="font-display text-xl font-bold text-[#60A5FA]">${confirmed.amount?.toFixed(2)}</span> via{" "}
                <span className="font-bold">Venmo</span> or{" "}
                <span className="font-bold">Zelle</span> at your session.
              </div>
              <div className="mt-1 text-xs text-[#A1A1AA]">
                You'll be given the Venmo handle and Zelle info when you arrive.
              </div>
            </div>

            <div className="mt-10 flex flex-col sm:flex-row gap-3 justify-center">
              <Link
                to="/my-pack"
                data-testid="confirmed-portal-btn"
                className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white px-6 py-3 text-sm font-bold tracking-widest uppercase"
              >
                Customer Portal
              </Link>
              <button
                onClick={() => {
                  setConfirmed(null);
                  setSlot("");
                  setSelectedDate(null);
                }}
                data-testid="confirmed-book-another-btn"
                className="border border-white/20 hover:border-[#2563EB] hover:text-[#60A5FA] px-6 py-3 text-sm font-bold tracking-widest uppercase"
              >
                Book Another
              </button>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div data-testid="booking-page" className="bg-[#050505] text-white min-h-screen">
      <Navbar />
      <main className="pt-28 pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link
            to="/"
            data-testid="booking-back-link"
            className="inline-flex items-center gap-2 text-xs tracking-widest uppercase text-white/60 hover:text-white"
          >
            <ArrowLeft size={14} /> Back
          </Link>

          <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16">
            <div>
              <div className="text-[10px] tracking-[0.3em] uppercase text-[#60A5FA]">
                [ Book a Training Session ]
              </div>
              <h1 className="font-display mt-6 text-4xl md:text-5xl lg:text-6xl font-bold uppercase tracking-tight leading-[1.0]">
                Lock In Your <span className="text-[#2563EB]">Time</span>
              </h1>
              <p className="mt-6 text-[#A1A1AA] max-w-md">
                Pick a weekday and time. We train Mon–Fri, 12 PM – 5 PM. All
                sessions are 1 hour. Pay with Venmo or Zelle at your session.
              </p>

              <div className="mt-10 bg-[#070B14] border border-white/5 p-6">
                <div className="text-[10px] tracking-[0.3em] uppercase text-white/60 mb-4">
                  Select Date
                </div>
                <Calendar
                  data-testid="booking-calendar"
                  mode="single"
                  selected={selectedDate}
                  onSelect={setSelectedDate}
                  disabled={(d) => isPast(d) || isWeekend(d)}
                  className="text-white"
                />
                {dateStr && (
                  <div className="mt-4 text-xs tracking-wider uppercase text-[#60A5FA]">
                    Selected: {dateStr}
                  </div>
                )}
              </div>

              {dateStr && (
                <div className="mt-6 bg-[#070B14] border border-white/5 p-6">
                  <div className="text-[10px] tracking-[0.3em] uppercase text-white/60 mb-4">
                    Available Time Slots
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {availability.all_slots.map((s) => {
                      const isAvail = availability.available.includes(s);
                      const isSelected = slot === s;
                      return (
                        <button
                          key={s}
                          disabled={!isAvail}
                          onClick={() => setSlot(s)}
                          data-testid={`time-slot-${s.replace(/[: ]/g, "")}`}
                          className={`px-4 py-3 text-sm font-bold tracking-wider uppercase border transition-colors ${
                            isSelected
                              ? "bg-[#2563EB] border-[#2563EB] text-white"
                              : isAvail
                              ? "bg-transparent border-white/15 text-white hover:border-[#2563EB]"
                              : "bg-white/5 border-white/5 text-white/30 cursor-not-allowed line-through"
                          }`}
                        >
                          <Clock size={12} className="inline mr-1.5 -mt-0.5" />
                          {s}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            <form
              onSubmit={submit}
              data-testid="booking-form"
              className="bg-[#070B14] border border-white/5 p-8 md:p-10 self-start lg:sticky lg:top-28"
            >
              <div className="text-[10px] tracking-[0.3em] uppercase text-[#60A5FA] mb-6">
                [ Athlete Details ]
              </div>

              <div className="space-y-5">
                <Field label="Full Name">
                  <input
                    name="name"
                    value={form.name}
                    onChange={onChange}
                    data-testid="booking-name"
                    className="bh-input"
                    required
                  />
                </Field>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <Field label="Email">
                    <input
                      name="email"
                      type="email"
                      value={form.email}
                      onChange={onChange}
                      data-testid="booking-email"
                      className="bh-input"
                      required
                    />
                  </Field>
                  <Field label="Phone">
                    <input
                      name="phone"
                      value={form.phone}
                      onChange={onChange}
                      data-testid="booking-phone"
                      className="bh-input"
                      required
                    />
                  </Field>
                </div>
                <Field label="Program">
                  <select
                    name="program"
                    value={form.program}
                    onChange={onChange}
                    data-testid="booking-program"
                    className="bh-input"
                  >
                    {PROGRAMS.map((p) => (
                      <option key={p.name} value={p.name} className="bg-[#070B14]">
                        {p.name} · ${p.price}/hr
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="Notes (optional)">
                  <textarea
                    name="notes"
                    value={form.notes}
                    onChange={onChange}
                    data-testid="booking-notes"
                    className="bh-input min-h-[100px] resize-y"
                    placeholder="Age, position, goals…"
                  />
                </Field>

                <div className="bg-[#050505] border border-white/5 p-4 text-sm">
                  <div className="text-[10px] tracking-[0.3em] uppercase text-white/50 mb-2">
                    Order Summary
                  </div>
                  <div className="flex items-center justify-between text-white">
                    <span>{dateStr || "— select a date —"} {slot && `· ${slot}`}</span>
                    <span className="text-[#60A5FA] font-display text-xl font-bold">
                      ${selectedProgram?.price ?? 0}
                    </span>
                  </div>
                  <div className="mt-1 text-xs text-white/50">
                    {form.program} · 1 hour · Pay via Venmo or Zelle at session
                  </div>
                </div>
              </div>

              <button
                type="submit"
                disabled={submitting}
                data-testid="booking-submit-btn"
                className="mt-8 w-full inline-flex items-center justify-center gap-2 bg-[#2563EB] hover:bg-[#1D4ED8] disabled:opacity-60 text-white font-bold tracking-widest uppercase text-sm px-8 py-4 transition-colors rounded-sm"
              >
                {submitting ? "Booking…" : "Confirm Booking"}
              </button>
              <div className="mt-3 text-center text-[10px] tracking-[0.25em] uppercase text-white/40">
                Pay with Venmo or Zelle at your session
              </div>
            </form>
          </div>
        </div>
      </main>
      <Footer />

      <style>{`
        .bh-input { width:100%; background:#050505; border:1px solid rgba(255,255,255,0.08); color:#fff; padding:14px 16px; font-size:14px; outline:none; transition:border-color 200ms ease; border-radius:2px; font-family:inherit; }
        .bh-input:focus { border-color: #2563EB; }
        .bh-input::placeholder { color: rgba(255,255,255,0.3); }
      `}</style>
    </div>
  );
}

const Field = ({ label, children }) => (
  <label className="block">
    <span className="text-[10px] tracking-[0.3em] uppercase text-white/60">{label}</span>
    <div className="mt-2">{children}</div>
  </label>
);
